import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import styles from "./FormError.module.css";

/**
 * What a form says when a submit did not go through.
 *
 * It is both an `alert` and a focus target. The alert is what a screen-reader user hears
 * without moving; the focus is what puts a keyboard user *at* the explanation instead of
 * leaving them at the button they just pressed, with the reason somewhere above them on a
 * page they now have to hunt through.
 *
 * `requestId` is shown when core sent one. It is the one value that lets support find the
 * exact request in the logs, and it is meaningless to anyone else — printing it costs a
 * line and saves a conversation.
 */
export function FormError({
  title,
  requestId,
  children,
}: {
  title: string;
  requestId?: string | null | undefined;
  children?: ReactNode | undefined;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Keyed on the title so a *different* failure moves focus again, while a re-render of the
  // same one does not yank focus away from someone already fixing a field.
  useEffect(() => {
    ref.current?.focus();
  }, [title]);

  return (
    <div className={styles.alert} role="alert" tabIndex={-1} ref={ref}>
      <p className={styles.title}>
        <svg className={styles.marker} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M8 4.6v4.2M8 11v.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        {title}
      </p>
      {children === undefined ? null : <div className={styles.body}>{children}</div>}
      {requestId === null || requestId === undefined ? null : (
        <p className={styles.requestId}>
          Reference <span className="figure">{requestId}</span>
        </p>
      )}
    </div>
  );
}
